// React Dependencies
import React, { useState, useRef, useCallback } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { IconButton } from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../../components/Common/MText';
import MButton from '../../../../components/MButton';
import Loading from '../../../../components/Loading';
import Colours from '../../../../styles/colours';
import ApiService from '../../../../services/apiService';
import Constants, { IMAGE_FORMATS } from '../../../../common/constants';
import { IsEmpty, IsFunction } from '../../../../common/checks';
import Icons from '../../../../components/Common/Icons';
import { MAX_FILE_SIZE, MAX_FILES } from '../SnapstyrComs/SnapstyrConstants';

const AlbumPhotosAdd = (props) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const navigateBack = () => {
    if (!IsEmpty(props.onClose) && IsFunction(props.onClose)) {
      props.onClose();
    }
  };

  const isValidFile = (file) => {
    if (!IMAGE_FORMATS.includes(file.type)) {
      return false;
    }

    const maxSizeBytes = MAX_FILE_SIZE * Constants.BinaryOneMBSize;
    if (file.size > maxSizeBytes) {
      toast.info(`${file.name} exceeds the ${MAX_FILE_SIZE}MB limit and was skipped`);
      return false;
    }

    return true;
  };

  const addFiles = useCallback((newFiles) => {
    const validFiles = newFiles.filter(isValidFile);

    if (IsEmpty(validFiles)) return;

    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      toast.info(`You can only upload a maximum of ${MAX_FILES} photos`);
      return;
    }

    const filesToAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      toast.info(`Only ${remaining} more photo(s) can be added (max ${MAX_FILES})`);
    }

    const mapped = filesToAdd.map((f) => ({
      file: f,
      url:  URL.createObjectURL(f),
      name: f.name,
    }));

    setFiles((prev) => [...prev, ...mapped]);
  }, [files.length]);

  const removeFile = (index) => {
    setFiles((prev) => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].url);
      copy.splice(index, 1);
      return copy;
    });
  };

  const onBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileInputChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // Drag-and-drop handlers
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const onUpload = async () => {
    if (IsEmpty(files)) {
      toast.error('Please select at least one photo');
      return;
    }

    try {
      setIsUploading(true);

      const formData = new FormData();

      for (const f of files) {
        formData.append('originalImages', f.file);
      }

      await ApiService.uploadPageAlbumPhotos(props.page.id, props.albumId, formData);
      toast.success('Photos uploaded');
      if (!IsEmpty(props.onComplete) && IsFunction(props.onComplete)) {
        props.onComplete();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="container-fluid"
      style={{ minHeight: '100dvh', backgroundColor: Colours.White }}
    >
      {isUploading && <Loading zIndex={9999} />}

      <div className="row">
        <div className="col-12 px-2 px-lg-3">

          {/* Header */}
          <div className="mt-3 d-flex flex-row align-items-center justify-content-between">
            <MText size={14} colour={Colours.Gray8} text="Album Photos Upload" />
            <IconButton onClick={navigateBack}>
              <Icons.Cross colour={Colours.Black} fontSize="1rem" />
            </IconButton>
          </div>

          {/* Dropzone */}
          {files.length < MAX_FILES && (
            <>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                className="d-flex flex-column align-items-center justify-content-center mt-3"
                style={{
                  border:          `2px dashed ${isDragging ? Colours.Secondary : Colours.LightGray4}`,
                  borderRadius:    12,
                  padding:         '40px 20px',
                  backgroundColor: isDragging ? 'rgba(0,0,0,0.02)' : Colours.White,
                  cursor:          'pointer',
                  transition:      'all 0.2s ease',
                }}
                onClick={onBrowseClick}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
              >
                <Add style={{ fontSize: 40, color: Colours.LightGray4, marginBottom: 8 }} />
                <MText text="Drag & drop photos here" size={14} colour={Colours.DarkGray} />
                <MText text="or click to browse" size={12} colour={Colours.LightGray4} className="mt-1" />
                <MText text={`Max ${MAX_FILES} photos, ${MAX_FILE_SIZE}MB each (JPG, PNG)`} size={11} colour={Colours.LightGray4} className="mt-2" />
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_FORMATS.join(',')}
                multiple
                style={{ display: 'none' }}
                onChange={onFileInputChange}
              />
            </>
          )}

          {/* Preview grid */}
          {!IsEmpty(files) && (
            <>
              <div className="d-flex flex-row justify-content-between align-items-center mt-4 mb-2">
                <MText text={`${files.length} photo${files.length > 1 ? 's' : ''} selected`} size={13} semibold />
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                {files.map((f, idx) => (
                  <div key={f.url} className="col-4 col-md-3" style={{ paddingRight: 6, paddingLeft: 6 }}>
                    <div
                      className="position-relative"
                      style={{
                        width:        '100%',
                        aspectRatio:  1,
                        borderRadius: 8,
                        overflow:     'hidden',
                        border:       `1px solid ${Colours.LightGray4}`,
                      }}
                    >
                      <img
                        src={f.url}
                        alt={f.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <IconButton
                        size="small"
                        className="position-absolute"
                        style={{
                          top:             4,
                          right:           4,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          padding:         3,
                        }}
                        onClick={() => removeFile(idx)}
                      >
                        <Close style={{ fontSize: 14, color: Colours.White }} />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-center mt-4 mb-5 pb-5">
                <MButton
                  text="Upload"
                  colour={Colours.Secondary}
                  textColour={Colours.White}
                  className="px-5"
                  onClick={onUpload}
                  disabled={isUploading || IsEmpty(files)}
                />
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

AlbumPhotosAdd.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  albumId:    PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose:    PropTypes.func,
  onComplete: PropTypes.func,
};

AlbumPhotosAdd.defaultProps = {
  onClose:    () => {},
  onComplete: () => {},
};

const mapStateToProps = (state) => ({
  page: state.page.selected,
});

export default connect(mapStateToProps, null)(AlbumPhotosAdd);
